import { CommonModule } from '@angular/common';
import { 
  ChangeDetectionStrategy,
  Component, 
  inject, 
  OnInit, 
  OnDestroy, 
  signal
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';

import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';

import { TabGroupComponent } from '../tab-group/tab-group.component';

import { FilterType, MenuItem, TabButtonGroup } from '../../models';
import { resetExpandedState, filterMenuItems } from '../../utils/menu-filter';
import { MenuStateService } from '../../../core/services/menu-state.service';

import { ResponseData } from '@shared/helpers/response-data';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-side-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule, MatInputModule, RouterModule, TabGroupComponent],
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideMenuComponent implements OnInit, OnDestroy {
  readonly router = inject(Router)

  readonly menuStateService = inject(MenuStateService);
  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });
  isHomePage = signal<boolean>(false);

  #searchTermSubject = new BehaviorSubject<string>('');
  modelSearchTerm: string = '';

  menuData = signal<ResponseData<MenuItem[]>>({
    data: null,
    status: 'loading'
  });
  filteredMenuItems = signal<MenuItem[]>([]);

  expandedMenuItems = signal<boolean>(false);
  selectedCategory = signal<FilterType>('all');
  tabButtonGroup = signal<TabButtonGroup[]>([
    { label: 'Favorites', value: 'favorites', selected: false },
    { label: 'All Categories', value: 'all', selected: true }
  ]);

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.isHomePage.set(this.router.url.includes('home'));
    this.#listenerMenuItems();
    this.#listenerSearchTerm();
  }

  #listenerMenuItems(): void {
    this.menuStateService.menuItems$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.menuData.set({
        data: res.data,
        status: 'success'
      });
      if (res.data) {
        const currentCategory = this.selectedCategory();
        const currentSearchTerm = this.modelSearchTerm;

        if (currentSearchTerm) {
          const filteredItems = filterMenuItems(res.data, currentSearchTerm, currentCategory);
          this.filteredMenuItems.set(filteredItems);
        } else if (currentCategory === 'favorites') {
          const filteredItems = filterMenuItems(res.data, '', 'favorites');
          this.filteredMenuItems.set(filteredItems);
        } else {
          this.filteredMenuItems.set(res.data);
        }
      } else {
        this.filteredMenuItems.set([]);
      }
    });
  }

  #listenerSearchTerm(): void {
    this.#searchTermSubject
      .pipe(skip(1), distinctUntilChanged(), debounceTime(100), takeUntil(this.destroy$))
      .subscribe((term) => {
        const formattedTerm = term.toLowerCase().trim();
        this.#filterMenuItemsBySearchTerm(formattedTerm);
      });
  }

  #filterMenuItemsBySearchTerm(term: string): void {
    const menuItems = this.menuData().data || [];

    if (!term && this.selectedCategory() === 'all') {
      return this.filteredMenuItems.set(resetExpandedState(menuItems));
    }

    const filteredItems = filterMenuItems(menuItems, term, this.selectedCategory());
    this.filteredMenuItems.set(filteredItems);
  }

  filterMenuItemsByCategory(categorie: FilterType): void {
    this.selectedCategory.set(categorie);
    this.modelSearchTerm = '';
    if (categorie === 'all') this.expandedMenuItems.set(false);
    const menuItems = this.menuData().data || [];
    const filteredItems = filterMenuItems(menuItems, '', categorie);
    this.filteredMenuItems.set(filteredItems);
  }

  onSearchTermChange(newValue: string): void {
    this.modelSearchTerm = newValue;
    this.#searchTermSubject.next(newValue);
  }

  toggleExpand(clickedItem: MenuItem): void {
    if (!!(clickedItem.items && clickedItem.items.length > 0)) {
      this.filteredMenuItems.update((current) => this.#mapAndToggleItem(current, clickedItem));
    }
  }

  #mapAndToggleItem(menuItems: MenuItem[], clickedItem: MenuItem): MenuItem[] {
    return menuItems.map((item) => {
      if (item.title === clickedItem.title) {
        return { ...item, isExpanded: !item.isExpanded };
      }
      if (item.items && item.items.length > 0) {
        return { ...item, items: this.#mapAndToggleItem(item.items, clickedItem) };
      }
      return item;
    });
  }

  toggleAllExpanded(expand?: boolean): void {
    this.expandedMenuItems.update((current) => (expand ? expand : !current));
    this.filteredMenuItems.update((current) => this.#mapAndToggleAllItems(current, this.expandedMenuItems()));
  }

  #mapAndToggleAllItems(menuItems: MenuItem[], expand: boolean): MenuItem[] {
    return menuItems.map((item) => {
      const newItem: MenuItem = { ...item, isExpanded: expand };

      if (item.items && item.items.length > 0) {
        newItem.items = this.#mapAndToggleAllItems(item.items, expand);
      }
      return newItem;
    });
  }

  getItemClasses(item: MenuItem, currentLevel: number): { [key: string]: boolean } {
    return {
      [`item-level-${currentLevel}`]: true,
      'has-sub-items': !!(item.items && item.items.length > 0),
      'is-expanded': item.isExpanded === true
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.#searchTermSubject.complete();
  }
}
